import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Loader2, Pencil, Trash2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { chainApi } from "@/api/chain";
import {
  additionalRevenueApi,
  type AdditionalRevenueRow,
} from "@/api/additionalRevenue";

const CENTRAL_HOSPITAL_SELECT_CENTRAL = "CENTRAL";
const ADD_NEW = "__add_new__";

function todayLocalISODate(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function monthLocalISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function formatDateIst(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "numeric",
    month: "numeric",
    year: "numeric",
  });
}

function formatMoney(n: number): string {
  return `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function inferUnitFromHospitalName(name: string | null | undefined): string {
  if (name && /true\s*artis|artis\s*south\s*delhi/i.test(name)) return "LAJPAT_NAGAR";
  return "SEC_52";
}

function formatUnitLabel(unit: string | null | undefined): string {
  if (unit === "CENTRAL") return "Central";
  if (unit === "LAJPAT_NAGAR") return "TRUE ARTIS South Delhi";
  if (unit === "SEC_52") return "Rudram (Sec 52)";
  return unit || "—";
}

function paymentModeLabel(mode: string | null | undefined): string {
  if (!mode) return "—";
  return mode.replace(/_/g, " ");
}

export default function AdditionalRevenuePage() {
  const { user, isChainAdmin, activeHospitalId } = useAuth();
  const qc = useQueryClient();
  const hospitalId = activeHospitalId || user?.hospitalId || "";
  const role = user?.role;
  const canEdit = role === "HOSPITAL_ADMIN";
  const canMark = role === "HOSPITAL_ADMIN";
  const isSuperAdminView = role === "SUPER_ADMIN";
  const readOnly = isSuperAdminView;

  const lockedUnit = useMemo(() => {
    const name =
      user?.hospitals?.find((h) => h.id === hospitalId)?.name ||
      user?.hospitals?.[0]?.name ||
      "";
    return inferUnitFromHospitalName(name);
  }, [hospitalId, user?.hospitals]);

  const { data: chainHospitals = [] } = useQuery({
    queryKey: ["chain-group-hospitals-add-rev"],
    queryFn: async () => {
      const r = await chainApi.getGroup();
      const group = r.data?.data as { hospitals?: Array<{ id: string; name: string }> } | undefined;
      return group?.hospitals ?? [];
    },
    enabled: !!isChainAdmin,
    staleTime: 60_000,
  });

  const chainHospitalOptions = useMemo(() => {
    const opts = (chainHospitals || [])
      .filter((h) => h?.id && h?.name)
      .map((h) => ({
        value: h.id,
        label: h.name,
      }));
    return [...opts, { value: CENTRAL_HOSPITAL_SELECT_CENTRAL, label: "Central" }];
  }, [chainHospitals]);

  const defaultHospitalSelect = useMemo(() => {
    if (!isChainAdmin) return lockedUnit;
    const rudram = chainHospitalOptions.find((o) => /rudram/i.test(o.label));
    return rudram?.value ?? chainHospitalOptions[0]?.value ?? CENTRAL_HOSPITAL_SELECT_CENTRAL;
  }, [isChainAdmin, lockedUnit, chainHospitalOptions]);

  const [hospitalSelect, setHospitalSelect] = useState(CENTRAL_HOSPITAL_SELECT_CENTRAL);
  useEffect(() => {
    setHospitalSelect(isChainAdmin ? defaultHospitalSelect : lockedUnit);
  }, [isChainAdmin, defaultHospitalSelect, lockedUnit]);

  const { data: meta } = useQuery({
    queryKey: ["additional-revenue-meta"],
    queryFn: () => additionalRevenueApi.meta(),
  });

  const { data: typeNames = [], isLoading: typesLoading } = useQuery({
    queryKey: ["additional-revenue-types"],
    queryFn: async () => (await additionalRevenueApi.listLookups("REVENUE_TYPE")).names || [],
  });

  const { data: sourceNames = [], isLoading: sourcesLoading } = useQuery({
    queryKey: ["additional-revenue-sources"],
    queryFn: async () => (await additionalRevenueApi.listLookups("REVENUE_SOURCE")).names || [],
  });

  const { data: rows = [], isLoading: listLoading } = useQuery({
    queryKey: ["additional-revenue-list", hospitalId],
    queryFn: () => additionalRevenueApi.list(),
    enabled: !!hospitalId,
  });

  const [revenueType, setRevenueType] = useState("");
  const [revenueSource, setRevenueSource] = useState("");
  const [amount, setAmount] = useState("");
  const [paymentMode, setPaymentMode] = useState("");
  const [revenueDate, setRevenueDate] = useState(todayLocalISODate());
  const [serviceMonth, setServiceMonth] = useState(monthLocalISO());
  const [invoiceNo, setInvoiceNo] = useState("");
  const [description, setDescription] = useState("");

  const [addDialog, setAddDialog] = useState<{
    kind: "REVENUE_TYPE" | "REVENUE_SOURCE";
    open: boolean;
  }>({ kind: "REVENUE_TYPE", open: false });
  const [addName, setAddName] = useState("");

  const [editRow, setEditRow] = useState<AdditionalRevenueRow | null>(null);
  const [markPaidId, setMarkPaidId] = useState<string | null>(null);
  const [markPaidMode, setMarkPaidMode] = useState("");

  const paymentModes = meta?.paymentModes?.length
    ? meta.paymentModes
    : ["CASH", "CARD", "UPI", "BANK_TRANSFER", "CHEQUE", "INSURANCE"];

  const createLookupMut = useMutation({
    mutationFn: async () => {
      const name = addName.trim();
      if (!name) throw new Error("Name is required");
      const body: {
        kind: "REVENUE_TYPE" | "REVENUE_SOURCE";
        name: string;
        targetHospitalId?: string;
        hospitalUnit?: string;
      } = { kind: addDialog.kind, name };
      if (isChainAdmin) {
        if (hospitalSelect === CENTRAL_HOSPITAL_SELECT_CENTRAL) {
          body.hospitalUnit = "CENTRAL";
        } else {
          body.targetHospitalId = hospitalSelect;
        }
      }
      await additionalRevenueApi.createLookup({
        kind: addDialog.kind,
        name,
        ...(body.targetHospitalId ? { hospitalId: body.targetHospitalId } : {}),
        ...(body.hospitalUnit ? { hospitalUnit: body.hospitalUnit } : {}),
      });
      return { kind: addDialog.kind, name };
    },
    onSuccess: ({ kind, name }) => {
      toast.success("Added to dropdown");
      setAddDialog((d) => ({ ...d, open: false }));
      setAddName("");
      if (kind === "REVENUE_TYPE") {
        setRevenueType(name);
        void qc.invalidateQueries({ queryKey: ["additional-revenue-types"] });
      } else {
        setRevenueSource(name);
        void qc.invalidateQueries({ queryKey: ["additional-revenue-sources"] });
      }
    },
    onError: (e: unknown) => {
      toast.error(e instanceof Error ? e.message : "Failed to add");
    },
  });

  const createMut = useMutation({
    mutationFn: async () => {
      const amt = Number(amount);
      if (!revenueType.trim()) throw new Error("Select or add a revenue type");
      if (!Number.isFinite(amt) || amt <= 0) throw new Error("Enter a valid amount");
      const body: Parameters<typeof additionalRevenueApi.create>[0] = {
        revenueType: revenueType.trim(),
        revenueSource: revenueSource.trim() || undefined,
        amount: amt,
        description: description.trim() || undefined,
        invoiceNo: invoiceNo.trim() || undefined,
        revenueDate: revenueDate || undefined,
        serviceMonth: serviceMonth || undefined,
        paymentMode: paymentMode || undefined,
      };
      if (isChainAdmin) {
        if (hospitalSelect === CENTRAL_HOSPITAL_SELECT_CENTRAL) {
          body.hospitalUnit = "CENTRAL";
        } else {
          body.targetHospitalId = hospitalSelect;
        }
      } else {
        body.hospitalUnit = lockedUnit;
      }
      return additionalRevenueApi.create(body);
    },
    onSuccess: () => {
      toast.success("Additional revenue saved");
      setAmount("");
      setDescription("");
      setInvoiceNo("");
      setPaymentMode("");
      void qc.invalidateQueries({ queryKey: ["additional-revenue-list"] });
      void qc.invalidateQueries({ queryKey: ["additional-revenue-types"] });
      void qc.invalidateQueries({ queryKey: ["additional-revenue-sources"] });
    },
    onError: (e: unknown) => {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (e instanceof Error ? e.message : "Failed to save");
      toast.error(msg);
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => additionalRevenueApi.remove(id),
    onSuccess: () => {
      toast.success("Deleted");
      void qc.invalidateQueries({ queryKey: ["additional-revenue-list"] });
    },
    onError: (e: unknown) => {
      toast.error(
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          "Delete failed"
      );
    },
  });

  const markMut = useMutation({
    mutationFn: ({ id, mode }: { id: string; mode: string }) =>
      additionalRevenueApi.markReceived(id, mode),
    onSuccess: () => {
      toast.success("Marked received");
      setMarkPaidId(null);
      setMarkPaidMode("");
      void qc.invalidateQueries({ queryKey: ["additional-revenue-list"] });
    },
    onError: (e: unknown) => {
      toast.error(
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          "Failed"
      );
    },
  });

  const unmarkMut = useMutation({
    mutationFn: (id: string) => additionalRevenueApi.unmarkReceived(id),
    onSuccess: () => {
      toast.success("Marked unpaid");
      void qc.invalidateQueries({ queryKey: ["additional-revenue-list"] });
    },
    onError: (e: unknown) => {
      toast.error(
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          "Failed"
      );
    },
  });

  const updateMut = useMutation({
    mutationFn: async () => {
      if (!editRow) throw new Error("Nothing to edit");
      const amt = Number(amount);
      if (!revenueType.trim()) throw new Error("Revenue type required");
      if (!Number.isFinite(amt) || amt <= 0) throw new Error("Enter a valid amount");
      return additionalRevenueApi.update(editRow.id, {
        revenueType: revenueType.trim(),
        revenueSource: revenueSource.trim() || undefined,
        amount: amt,
        description: description.trim() || undefined,
        invoiceNo: invoiceNo.trim() || undefined,
        revenueDate: revenueDate || undefined,
        serviceMonth: serviceMonth || undefined,
      });
    },
    onSuccess: () => {
      toast.success("Updated");
      setEditRow(null);
      resetForm();
      void qc.invalidateQueries({ queryKey: ["additional-revenue-list"] });
    },
    onError: (e: unknown) => {
      toast.error(
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          "Update failed"
      );
    },
  });

  function resetForm() {
    setRevenueType("");
    setRevenueSource("");
    setAmount("");
    setPaymentMode("");
    setRevenueDate(todayLocalISODate());
    setServiceMonth(monthLocalISO());
    setInvoiceNo("");
    setDescription("");
  }

  function startEdit(row: AdditionalRevenueRow) {
    setEditRow(row);
    setRevenueType(row.revenueType);
    setRevenueSource(row.revenueSource || "");
    setAmount(String(row.amount));
    setRevenueDate(row.revenueDate?.slice(0, 10) || todayLocalISODate());
    setServiceMonth(
      row.serviceMonth
        ? `${new Date(row.serviceMonth).getUTCFullYear()}-${String(new Date(row.serviceMonth).getUTCMonth() + 1).padStart(2, "0")}`
        : monthLocalISO()
    );
    setInvoiceNo(row.invoiceNo || "");
    setDescription(row.description || "");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function onTypeSelect(v: string) {
    if (v === ADD_NEW) {
      setAddDialog({ kind: "REVENUE_TYPE", open: true });
      setAddName("");
      return;
    }
    setRevenueType(v === "__none__" ? "" : v);
  }

  function onSourceSelect(v: string) {
    if (v === ADD_NEW) {
      setAddDialog({ kind: "REVENUE_SOURCE", open: true });
      setAddName("");
      return;
    }
    setRevenueSource(v === "__none__" ? "" : v);
  }

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Additional Revenue</h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
          Book income outside OPD, IPD, Lab, Pharma, Day Care, and Additional Services. Amounts flow into
          Hospital P&amp;L (A13A by service month), Cash Flow (CF11A when received), and books (Other Income).
        </p>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">
            {editRow ? "Edit additional revenue" : "New additional revenue"}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label>Revenue type</Label>
              <Select value={revenueType || "__none__"} onValueChange={onTypeSelect}>
                <SelectTrigger>
                  <SelectValue
                    placeholder={typesLoading ? "Loading…" : "Select type (or add new)"}
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__" disabled>
                    Select type
                  </SelectItem>
                  {typeNames.map((n) => (
                    <SelectItem key={n} value={n}>
                      {n}
                    </SelectItem>
                  ))}
                  <SelectItem value={ADD_NEW}>Add new type…</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Starts empty. Use “Add new type” once — it appears next time.
              </p>
            </div>

            <div className="space-y-2">
              <Label>Hospital / unit</Label>
              {isChainAdmin ? (
                <Select value={hospitalSelect} onValueChange={setHospitalSelect}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {chainHospitalOptions.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input value={formatUnitLabel(lockedUnit)} disabled />
              )}
              <p className="text-xs text-muted-foreground">
                Chain admin: pick a hospital, or Central for chain-level revenue.
              </p>
            </div>

            <div className="space-y-2">
              <Label>Amount (₹)</Label>
              <Input
                type="number"
                min={0}
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>Mode of payment (if already received)</Label>
              <Select
                value={paymentMode || "__none__"}
                onValueChange={(v) => setPaymentMode(v === "__none__" ? "" : v)}
                disabled={!!editRow}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Not received yet" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Not received yet</SelectItem>
                  {paymentModes.map((m) => (
                    <SelectItem key={m} value={m}>
                      {paymentModeLabel(m)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                If you select a mode, this revenue is saved as Received immediately.
              </p>
            </div>

            <div className="space-y-2">
              <Label>Revenue date</Label>
              <Input
                type="date"
                value={revenueDate}
                onChange={(e) => setRevenueDate(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>Service month</Label>
              <Input
                type="month"
                value={serviceMonth}
                onChange={(e) => setServiceMonth(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">Month for P&amp;L accrual.</p>
            </div>

            <div className="space-y-2">
              <Label>Revenue source</Label>
              <Select value={revenueSource || "__none__"} onValueChange={onSourceSelect}>
                <SelectTrigger>
                  <SelectValue
                    placeholder={sourcesLoading ? "Loading…" : "Optional (or add new)"}
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None</SelectItem>
                  {sourceNames.map((n) => (
                    <SelectItem key={n} value={n}>
                      {n}
                    </SelectItem>
                  ))}
                  <SelectItem value={ADD_NEW}>Add new source…</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Same as type — blank until you add items; then they stay in the list.
              </p>
            </div>

            <div className="space-y-2">
              <Label>Invoice / ref no.</Label>
              <Input value={invoiceNo} onChange={(e) => setInvoiceNo(e.target.value)} />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional notes"
            />
          </div>

          <div className="flex flex-wrap gap-2">
            {editRow ? (
              <>
                <Button
                  onClick={() => updateMut.mutate()}
                  disabled={updateMut.isPending || !canEdit || readOnly}
                >
                  {updateMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Update
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setEditRow(null);
                    resetForm();
                  }}
                >
                  Cancel
                </Button>
              </>
            ) : (
              <Button
                onClick={() => createMut.mutate()}
                disabled={createMut.isPending || !canMark || readOnly}
              >
                {createMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save
              </Button>
            )}
          </div>
          {readOnly && (
            <p className="text-sm text-muted-foreground">
              Super admin view only — use a hospital admin account to book or edit.
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Recent additional revenue</CardTitle>
        </CardHeader>
        <CardContent>
          {listLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-8">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : rows.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6">No additional revenue booked yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Revenue ID</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Hospital</TableHead>
                    <TableHead>Entered by</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell className="font-mono text-xs">
                        {row.revenueNumber || "—"}
                      </TableCell>
                      <TableCell>{formatDateIst(row.revenueDate)}</TableCell>
                      <TableCell>{row.revenueType}</TableCell>
                      <TableCell>{row.revenueSource || "—"}</TableCell>
                      <TableCell>
                        {row.hospitalUnit === "CENTRAL"
                          ? "Central"
                          : row.hospital?.name || formatUnitLabel(row.hospitalUnit)}
                      </TableCell>
                      <TableCell>{row.createdByName || "—"}</TableCell>
                      <TableCell className="text-right">{formatMoney(row.amount)}</TableCell>
                      <TableCell>
                        {row.paymentStatus === "PAID" ? (
                          <Badge variant="outline" className="text-emerald-800 border-emerald-300">
                            Received ({paymentModeLabel(row.paymentMode)})
                          </Badge>
                        ) : (
                          <Badge variant="outline">Unpaid</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {canEdit && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => startEdit(row)}
                            >
                              <Pencil className="h-3.5 w-3.5 mr-1" />
                              Edit
                            </Button>
                          )}
                          {canMark && row.paymentStatus !== "PAID" && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setMarkPaidId(row.id);
                                setMarkPaidMode("");
                              }}
                            >
                              Mark received
                            </Button>
                          )}
                          {canEdit &&
                            row.paymentStatus === "PAID" && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => unmarkMut.mutate(row.id)}
                                disabled={unmarkMut.isPending}
                              >
                                Mark unpaid
                              </Button>
                            )}
                          {canEdit && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-destructive"
                              onClick={() => {
                                if (confirm("Delete this additional revenue entry?")) {
                                  deleteMut.mutate(row.id);
                                }
                              }}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={addDialog.open}
        onOpenChange={(open) => setAddDialog((d) => ({ ...d, open }))}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Add {addDialog.kind === "REVENUE_TYPE" ? "revenue type" : "revenue source"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label>Name</Label>
            <Input
              value={addName}
              onChange={(e) => setAddName(e.target.value)}
              placeholder={
                addDialog.kind === "REVENUE_TYPE"
                  ? "e.g. Training fees, Rental income"
                  : "e.g. Corporate, Partner, Government"
              }
              onKeyDown={(e) => {
                if (e.key === "Enter") createLookupMut.mutate();
              }}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialog((d) => ({ ...d, open: false }))}>
              Cancel
            </Button>
            <Button onClick={() => createLookupMut.mutate()} disabled={createLookupMut.isPending}>
              {createLookupMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!markPaidId} onOpenChange={(o) => !o && setMarkPaidId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark received</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label>Mode of payment</Label>
            <Select value={markPaidMode || "__none__"} onValueChange={(v) => setMarkPaidMode(v === "__none__" ? "" : v)}>
              <SelectTrigger>
                <SelectValue placeholder="Select mode" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__" disabled>
                  Select mode
                </SelectItem>
                {paymentModes.map((m) => (
                  <SelectItem key={m} value={m}>
                    {paymentModeLabel(m)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMarkPaidId(null)}>
              Cancel
            </Button>
            <Button
              disabled={!markPaidMode || !markPaidId || markMut.isPending}
              onClick={() => {
                if (markPaidId && markPaidMode) {
                  markMut.mutate({ id: markPaidId, mode: markPaidMode });
                }
              }}
            >
              {markMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
